/**
 * AbilityIconLoader Service (L4-ABILITY-ICONS-002)
 *
 * Singleton service to preload and cache ability icon textures.
 * Manages PIXI textures for ability icons based on element type.
 */

import * as PIXI from 'pixi.js';

/**
 * Supported ability element types
 */
export const ABILITY_ELEMENTS = [
  'physical',
  'fire',
  'ice',
  'lightning',
  'poison',
  'dark',
  'holy',
  'arcane',
  'nature',
  'buff',
] as const;

export type AbilityElement = (typeof ABILITY_ELEMENTS)[number];

/**
 * AbilityIconLoader
 *
 * Handles preloading and caching of ability icon textures.
 * Provides synchronous access to textures after preloading is complete.
 *
 * @example
 * ```typescript
 * // Preload icons at startup
 * await abilityIconLoader.preloadAbilityIcons();
 *
 * // Get texture for an ability
 * const texture = abilityIconLoader.getIconTexture('fire');
 * if (texture) {
 *   const sprite = new PIXI.Sprite(texture);
 * }
 *
 * // For buff delivery type, get buff icon
 * const buffTexture = abilityIconLoader.getIconTexture('fire', 'buff');
 * ```
 */
class AbilityIconLoader {
  /** Cache of loaded textures by element name */
  private textureCache: Map<string, PIXI.Texture> = new Map();

  /** Flag indicating if preload is complete */
  private loaded: boolean = false;

  /** Base path for ability icons */
  private readonly iconBasePath = '/assets/ability-icons';

  /**
   * Preload all ability icon textures.
   *
   * Loads textures for all supported elements from the assets directory.
   * Failed loads are logged as warnings but don't prevent other icons from loading.
   *
   * @returns Promise that resolves when all icons are loaded (or failed gracefully)
   */
  async preloadAbilityIcons(): Promise<void> {
    const loadPromises: Promise<void>[] = [];

    for (const element of ABILITY_ELEMENTS) {
      const iconPath = `${this.iconBasePath}/${element}.png`;

      const loadPromise = PIXI.Assets.load<PIXI.Texture>(iconPath)
        .then((texture) => {
          this.textureCache.set(element, texture);
        })
        .catch((error) => {
          console.warn(
            `[AbilityIconLoader] Failed to load icon for ${element}:`,
            error instanceof Error ? error.message : error
          );
          // Continue loading other icons
        });

      loadPromises.push(loadPromise);
    }

    await Promise.all(loadPromises);
    this.loaded = true;

    console.log(
      `[AbilityIconLoader] Preload complete: ${this.textureCache.size}/${ABILITY_ELEMENTS.length} icons loaded`
    );
  }

  /**
   * Get the texture for an ability element.
   *
   * @param element - The element type (e.g., 'fire', 'ice', 'poison')
   * @param delivery - Optional delivery type. If 'buff', returns the buff icon instead.
   * @returns The cached texture, or null if not found
   *
   * @example
   * ```typescript
   * // Get fire icon
   * const fireTexture = loader.getIconTexture('fire');
   *
   * // For buff abilities, get the buff icon regardless of element
   * const buffTexture = loader.getIconTexture('fire', 'buff');
   * ```
   */
  getIconTexture(element: string, delivery?: string): PIXI.Texture | null {
    // For buff delivery type, always return the buff icon
    if (delivery === 'buff') {
      return this.textureCache.get('buff') || null;
    }

    return this.textureCache.get(element) || null;
  }

  /**
   * Check if preloading is complete.
   *
   * @returns True if preloadAbilityIcons() has completed
   */
  isLoaded(): boolean {
    return this.loaded;
  }

  /**
   * Get the number of loaded textures.
   * Useful for debugging and progress tracking.
   *
   * @returns Number of successfully loaded textures
   */
  getLoadedCount(): number {
    return this.textureCache.size;
  }

  /**
   * Clear the texture cache and reset loaded state.
   * Useful for testing or when reloading assets.
   */
  reset(): void {
    this.textureCache.clear();
    this.loaded = false;
  }
}

// Singleton instance
let loaderInstance: AbilityIconLoader | null = null;

/**
 * Get the singleton AbilityIconLoader instance.
 *
 * @returns The shared AbilityIconLoader instance
 */
export function getAbilityIconLoader(): AbilityIconLoader {
  if (!loaderInstance) {
    loaderInstance = new AbilityIconLoader();
  }
  return loaderInstance;
}

/**
 * Preload all ability icons.
 * Convenience function that delegates to the singleton instance.
 *
 * @returns Promise that resolves when preloading is complete
 */
export async function preloadAbilityIcons(): Promise<void> {
  return getAbilityIconLoader().preloadAbilityIcons();
}

/**
 * Get an ability icon texture.
 * Convenience function that delegates to the singleton instance.
 *
 * @param element - The element type
 * @param delivery - Optional delivery type
 * @returns The texture or null if not found
 */
export function getIconTexture(element: string, delivery?: string): PIXI.Texture | null {
  return getAbilityIconLoader().getIconTexture(element, delivery);
}

/**
 * Check if ability icons are loaded.
 * Convenience function that delegates to the singleton instance.
 *
 * @returns True if preloading is complete
 */
export function isLoaded(): boolean {
  return getAbilityIconLoader().isLoaded();
}

// Export the class for testing purposes
export { AbilityIconLoader };
